import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, RefreshCw, UserPlus, Link2, FolderPlus } from 'lucide-react';
import { toast } from 'sonner';

export default function DealRoomIntegration({ companyId }: { companyId: string }) {
  const [deals, setDeals] = useState<any[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [selectedDeal, setSelectedDeal] = useState('');
  const [folder, setFolder] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);

  async function loadData() {
    setLoading(true);
    const [{ data: d }, { data: f }] = await Promise.all([
      supabase
        .from('deal_rooms')
        .select('id,name,deal_type,stage,folder_linked,participant_count')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false }),
      supabase
        .from('data_room_files')
        .select('folder')
        .eq('company_id', companyId)
        .neq('folder', 'root'),
    ]);
    setDeals(d || []);
    const uniqueFolders = [...new Set((f || []).map((x: any) => x.folder).filter(Boolean))];
    setFolders(uniqueFolders);
    setLoading(false);
  }

  async function linkFolder() {
    if (!selectedDeal || !folder) {
      toast.error('Select deal and folder');
      return;
    }

    const { error } = await supabase.functions.invoke('link-folder-to-deal', {
      body: { companyId, dealId: selectedDeal, folder },
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Folder linked to deal');
      loadData();
    }
  }

  async function addInvestor() {
    if (!email || !selectedDeal) {
      toast.error('Select deal and enter email');
      return;
    }

    setAssigning(true);
    const { error } = await supabase.functions.invoke('add-deal-participant', {
      body: { companyId, dealId: selectedDeal, email },
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Investor added');
      setEmail('');
      loadData();
    }
    setAssigning(false);
  }

  useEffect(() => {
    loadData();
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Deal Room Integration</CardTitle>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : deals.length ? (
          <>
            <div className="grid sm:grid-cols-4 gap-2">
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={selectedDeal}
                onChange={(e) => setSelectedDeal(e.target.value)}
              >
                <option value="">Select Deal</option>
                {deals.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.stage})
                  </option>
                ))}
              </select>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
              >
                <option value="">Select Folder</option>
                {folders.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              <Button onClick={linkFolder}>
                <FolderPlus className="h-4 w-4 mr-1" /> Link Folder
              </Button>
            </div>

            <div className="grid sm:grid-cols-3 gap-2 mt-4">
              <Input
                placeholder="Investor Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button onClick={addInvestor} disabled={assigning}>
                {assigning ? (
                  <Loader2 className="animate-spin h-4 w-4" />
                ) : (
                  <UserPlus className="h-4 w-4 mr-1" />
                )}
                Add Investor
              </Button>
            </div>

            <div className="mt-6">
              <h3 className="text-sm font-semibold mb-2">Active Deals</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Linked Folder</TableHead>
                    <TableHead>Investors</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deals.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{d.name}</TableCell>
                      <TableCell>{d.deal_type}</TableCell>
                      <TableCell>{d.stage}</TableCell>
                      <TableCell>{d.folder_linked || '—'}</TableCell>
                      <TableCell>{d.participant_count || 0}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/deal/${d.id}`, '_blank')}
                        >
                          <Link2 className="h-4 w-4 mr-1" /> Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">No deals found for this company.</p>
        )}
      </CardContent>
    </Card>
  );
}
